import { Chip, SxProps, Theme, Typography } from "@mui/material";
import useIsMobile from "components/hooks/useIsMobile";

export enum StageValue {
  New = "new",
  InQueue = "in_queue",
  Active = "active",
  AdvisorReview = "advisor_review",
  WaitingOnClient = "waiting_on_client",
  OnHold = "on_hold",
  Done = "done",
  Deleted = "deleted",
}

type StageOption = {
  value: StageValue;
  updateLimitedTo: "member" | "advisor" | "all";
  advisorVariant: "teal" | "red" | "gray" | "green" | "warning" | "outlined";
  memberVariant: "teal" | "red" | "gray" | "green" | "warning" | "outlined";
  advisorLabel: string;
  memberLabel: string;
  // If set, the first one is the default next step
  memberNextSteps?: StageValue[];
  advisorNextSteps?: StageValue[];
};
export const StageOptions: StageOption[] = [
  {
    value: StageValue.New,
    advisorVariant: "red", // "Needs review" shown as red in your diagram
    memberVariant: "gray", // "Submitted" shown as gray
    advisorLabel: "Needs review",
    memberLabel: "Submitted",
    updateLimitedTo: "all",
    memberNextSteps: [StageValue.OnHold, StageValue.Done],
    advisorNextSteps: [
      StageValue.InQueue,
      StageValue.Active,
      StageValue.WaitingOnClient,
      StageValue.OnHold,
      StageValue.Done,
    ],
  },
  {
    value: StageValue.InQueue,
    advisorVariant: "outlined",
    memberVariant: "outlined",
    advisorLabel: "In queue",
    memberLabel: "In queue",
    updateLimitedTo: "advisor",
    memberNextSteps: [StageValue.OnHold, StageValue.Done],
    advisorNextSteps: [
      StageValue.Active,
      StageValue.WaitingOnClient,
      StageValue.OnHold,
      StageValue.Done,
    ],
  },
  {
    value: StageValue.Active,
    advisorVariant: "teal",
    memberVariant: "teal",
    advisorLabel: "Active",
    memberLabel: "Active",
    updateLimitedTo: "all",
    memberNextSteps: [StageValue.Done, StageValue.OnHold],
    advisorNextSteps: [
      StageValue.Done,
      StageValue.WaitingOnClient,
      StageValue.OnHold,
      StageValue.InQueue,
    ],
  },
  {
    value: StageValue.AdvisorReview,
    advisorVariant: "red",
    memberVariant: "teal",
    advisorLabel: "Needs review",
    memberLabel: "Active", // Is this correct?
    updateLimitedTo: "member",
    memberNextSteps: [StageValue.Done, StageValue.OnHold],
    advisorNextSteps: [
      StageValue.Done,
      StageValue.Active,
      StageValue.WaitingOnClient,
      StageValue.OnHold,
      StageValue.InQueue,
      StageValue.Deleted,
    ],
  },
  {
    value: StageValue.WaitingOnClient,
    advisorVariant: "warning",
    memberVariant: "red",
    advisorLabel: "Client review",
    memberLabel: "Needs review",
    updateLimitedTo: "advisor",
    memberNextSteps: [
      StageValue.AdvisorReview,
      StageValue.OnHold,
      StageValue.Done,
    ],
    advisorNextSteps: [StageValue.Active, StageValue.OnHold, StageValue.Done],
  },
  {
    value: StageValue.OnHold,
    advisorVariant: "gray",
    memberVariant: "gray",
    advisorLabel: "On hold",
    memberLabel: "On hold",
    updateLimitedTo: "all",
    memberNextSteps: [
      StageValue.Active,
      StageValue.InQueue,
      StageValue.OnHold,
      StageValue.Done,
    ],
    advisorNextSteps: [
      StageValue.InQueue,
      StageValue.Active,
      StageValue.WaitingOnClient,
      StageValue.OnHold,
      StageValue.Done,
    ],
  },
  {
    value: StageValue.Done,
    advisorVariant: "green",
    memberVariant: "green",
    advisorLabel: "Done",
    memberLabel: "Done",
    updateLimitedTo: "all",
    memberNextSteps: [StageValue.Done],
    advisorNextSteps: [StageValue.Active],
  },
  {
    value: StageValue.Deleted,
    advisorVariant: "gray",
    memberVariant: "gray",
    advisorLabel: "Deleted",
    memberLabel: "Deleted",
    updateLimitedTo: "all",
    memberNextSteps: [StageValue.InQueue],
    advisorNextSteps: [StageValue.InQueue, StageValue.Active],
  },
];

export const AvailableStatusOptions = (
  currentStage: StageValue,
  userType: "member" | "advisor",
  withComment: boolean,
): {
  label: string;
  value: StageValue;
  variant: "teal" | "red" | "gray" | "green" | "warning" | "outlined";
  selectedTitle: string;
}[] => {
  const currentOption = StageOptions.find(
    (option) => option.value === currentStage,
  );
  const limitedNextSteps =
    currentOption &&
    (userType === "member"
      ? currentOption.memberNextSteps
      : currentOption.advisorNextSteps);
  return StageOptions.filter((option) => {
    if (option.updateLimitedTo === "all") return true;
    return option.updateLimitedTo === userType;
  })
    .filter((option) => {
      if (!limitedNextSteps) return true;
      // Always include the current stage so users can select it to go back to "Update status"
      if (option.value === currentStage) return true;
      return limitedNextSteps.includes(option.value);
    })
    .map((option) => {
      let displayLabel =
        userType === "member" ? option.memberLabel : option.advisorLabel;

      // Show "reviewed" instead of "Active" in the dropdown choices for the client
      if (option.value === StageValue.AdvisorReview && userType === "member") {
        displayLabel = "Reviewed";
      }

      return {
        value: option.value,
        label: displayLabel,
        variant:
          userType === "member" ? option.memberVariant : option.advisorVariant,
        selectedTitle: `${
          withComment ? "Comment as" : "Update to"
        } ${displayLabel}`,
      };
    });
};

const stageOptionMap = StageOptions.reduce(
  (acc, option) => {
    acc[option.value] = option;
    return acc;
  },
  {} as Record<StageValue, StageOption>,
);

export const AvailableFilterOptionsAdvisor: {
  label: string;
  value: StageValue;
  labelElement: React.ReactNode;
}[] = [
  {
    value: StageValue.New,
    label: "Needs initial review",
    labelElement: (
      <Chip
        size="small"
        label={"Needs initial review"}
        variant={stageOptionMap[StageValue.New].advisorVariant}
      />
    ),
  },
  {
    value: StageValue.InQueue,
    label: stageOptionMap[StageValue.InQueue].advisorLabel,
    labelElement: (
      <Chip
        size="small"
        label={stageOptionMap[StageValue.InQueue].advisorLabel}
        variant={stageOptionMap[StageValue.InQueue].advisorVariant}
      />
    ),
  },
  {
    value: StageValue.Active,
    label: stageOptionMap[StageValue.Active].advisorLabel,
    labelElement: (
      <Chip
        size="small"
        label={stageOptionMap[StageValue.Active].advisorLabel}
        variant={stageOptionMap[StageValue.Active].advisorVariant}
      />
    ),
  },
  {
    value: StageValue.AdvisorReview,
    label: stageOptionMap[StageValue.AdvisorReview].advisorLabel,
    labelElement: (
      <Chip
        size="small"
        label={stageOptionMap[StageValue.AdvisorReview].advisorLabel}
        variant={stageOptionMap[StageValue.AdvisorReview].advisorVariant}
      />
    ),
  },
  {
    value: StageValue.WaitingOnClient,
    label: stageOptionMap[StageValue.WaitingOnClient].advisorLabel,
    labelElement: (
      <Chip
        size="small"
        label={stageOptionMap[StageValue.WaitingOnClient].advisorLabel}
        variant={stageOptionMap[StageValue.WaitingOnClient].advisorVariant}
      />
    ),
  },
  {
    value: StageValue.OnHold,
    label: stageOptionMap[StageValue.OnHold].advisorLabel,
    labelElement: (
      <Chip
        size="small"
        label={stageOptionMap[StageValue.OnHold].advisorLabel}
        variant={stageOptionMap[StageValue.OnHold].advisorVariant}
      />
    ),
  },
];

export const StageChip = ({
  stage,
  size = "small",
  sx,
  accountType,
  stageDate,
}: {
  stage: string;
  stageDate?: string;
  accountType: "advisor" | "member";
  size?: "small" | "medium";
  sx?: SxProps<Theme>;
}) => {
  const stageOption =
    StageOptions.find((option) => option.value === stage) || StageOptions[0];
  let label =
    accountType === "advisor"
      ? stageOption.advisorLabel
      : stageOption.memberLabel;
  if (stageDate) {
    const date = new Date(stageDate);
    // format at "Jun 18"
    label += ` until ${date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
    })}`;
  }
  return (
    <Chip
      label={label}
      variant={
        accountType === "advisor"
          ? stageOption.advisorVariant
          : stageOption.memberVariant
      }
      size={size}
      sx={sx}
    />
  );
};

export const getScrollableParent = (
  element: HTMLElement | undefined,
): HTMLElement => {
  let parent = element?.parentElement;
  while (parent) {
    const overflowY = window.getComputedStyle(parent).overflowY;
    if (overflowY === "auto" || overflowY === "scroll") {
      return parent;
    }
    parent = parent.parentElement;
  }
  return document.body; // Fallback to window if none found
};

export const CurrentStageChip = ({
  clickTargetId,
  stage,
  accountType,
}: {
  clickTargetId: string;
  stage: StageValue;
  stageDate?: string;
  accountType: "advisor" | "member";
}) => {
  const isMobile = useIsMobile();
  const stageOption =
    StageOptions.find((option) => option.value === stage) || StageOptions[0];
  // Color mapping for text and border based on variant
  const colorMap: Record<string, string> = {
    teal: "#198282",
    red: "#D32F2F",
    gray: "#8E9598",
    green: "#198282",
    warning: "#9A6518",
  };

  return (
    <div
      onClick={() => {
        // Scroll to bottom of page and "click" #task-assignments-dropdown
        const dropdown = document.getElementById(clickTargetId);
        const scrollableParent = getScrollableParent(dropdown || undefined);
        // Need to use window on mobile
        (isMobile ? window : scrollableParent).scrollTo({
          top: scrollableParent.scrollHeight,
          behavior: "smooth",
        });
        if (dropdown) {
          // Add a delay so things scroll into view before clicking
          setTimeout(() => {
            dropdown.click();
          }, 800);
        }
      }}
      style={{
        border: "1px solid #D4D4D4",
        borderRadius: "100px",
        height: "40px",
        minHeight: "40px",
        color: "#8E9598",
        fontSize: "14px",
        fontWeight: "500",
        display: "inline-flex",
        alignItems: "center",
        background: "#FFF",
        padding: "8px 22px 8px 22px",
        cursor: "pointer",
      }}
    >
      <Typography variant="bodySmall">
        Status: {/* access MuiChip palette variances */}
        <span
          style={{
            color:
              colorMap[
                accountType === "advisor"
                  ? stageOption.advisorVariant
                  : stageOption.memberVariant
              ],
          }}
        >
          {accountType === "advisor"
            ? stageOption.advisorLabel
            : stageOption.memberLabel}
        </span>
      </Typography>
    </div>
  );
};
